<template>
  <div class="create-gallery-form">
    <div class="header">
      <h2>{{ gallery ? 'Edit board' : 'Create board' }}</h2>
      <button class="close-btn" @click="close">×</button>
    </div>

    <div class="content">
      <div class="form-group flex-1">
        <label for="title">Title</label>
        <input
            type="text"
            required
            class="mb-2"
            id="title"
            v-model="title"
            placeholder="Title *"
        />
      </div>
      <div class="form-group flex-1">
        <label for="description">Description</label>
        <textarea
            required
            class="mb-2"
            id="description"
            v-model="description"
            placeholder="Description"
        />
      </div>
    </div>
    <div class="footer">
      <div class="button-wrapper">
        <button class="cancel-btn" @click="cancel">Discard</button>
        <button class="create-btn" :disabled="!canSave" @click="saveOrCreateGallery">
          {{ gallery ? 'Save' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { defineEmits, defineProps } from "vue";
import { useWorkStore } from "~/composables/useWorkStore";
import * as pkg from 'vue-toastification';
import {useUserStore} from "~/composables/useUserStore";
const { useToast } = pkg;

const props = defineProps({
  gallery: {
    type: Object,
    default: null
  },
});
const {user} = useUserStore();

const emit = defineEmits(['close', 'updated']);
const toast = useToast();
const { patchGallery, createGallery } = useWorkStore();

// Initial values for editing or creating a gallery
const title = ref<string>(props.gallery ? props.gallery.title : '');
const description = ref<string>(props.gallery ? props.gallery.description : '');

// Track initial values
const initialTitle = ref(title.value);
const initialDescription = ref(description.value);

// Computed property to enable the button only when fields are filled and changed
const canSave = computed(() => {
  return title.value !== '' &&
      (title.value !== initialTitle.value || description.value !== initialDescription.value);
});

// Function to save or create the gallery (board)
const saveOrCreateGallery = async () => {
  if (props.gallery) {
    // Edit the existing gallery
    const payload = { title: title.value, description: description.value };
    try {
      await patchGallery(payload, props.gallery._id);
      toast.success('Board saved successfully');
      emit('updated', true); // Notify parent that the gallery has been updated
    } catch (error) {
      toast.error('Failed to save the board');
    }
  } else {
    // Create a new gallery
    const payload = {
      user: user.value._id,
      title: title.value,
      description: description.value,
      tag: 'new-board' // Use a default tag or modify based on requirements
    };
    try {
      await createGallery(payload);
      toast.success('New board created successfully');
      emit('updated', true); // Notify parent that a new gallery has been created
    } catch (error) {
      toast.error('Failed to create the new board');
    }
  }
};

const close = () => {
  emit('close');
};

const cancel = () => {
  emit('close');
};
</script>
<style scoped>
.create-gallery-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: black;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.header h2 {
  font-size: 28px;
  text-align: center;
  width: 100%;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
}

.content {
  display: flex;
  gap: 20px;
  padding: 0 40px;
  flex-direction: column;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
}

.footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.footer .button-wrapper {
  background: #EDEDED;
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  display: flex;
  justify-content: center;
}

.create-btn {
  background-color: #ccc;
  border: none;
  min-width: 140px;
  padding: 5px 0;
  border-radius: 25px;
  cursor: not-allowed;
  color: #b8b8b8;
}

.cancel-btn {
  background-color: #FAFAFA;
  border: none;
  min-width: 140px;
  padding: 5px 0;
  border-radius: 25px;
  cursor: not-allowed;
  color: #b8b8b8;
  margin-right: 20px;
}

.create-btn:enabled {
  background-color: #E3FF00;
  cursor: pointer;
  color: black;
}
</style>
